/**
 * asset-abilities-panel.tsx
 *
 * Abilities UI for an asset (Phase 1). Lets owners/editors configure:
 * - AI Search: enable, autoSyncOnChange, resync
 * - Content Resolution: enable, exposeContent, exposeDeps, depWhitelist, depAliases
 */
import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import abilityService, { type AiSearchAbilityState, type ContentResolutionAbilityState, type AbilitiesResponse } from '@/services/ability-service';
import type { AssetWithPermission } from '@/services/asset-service';
import { AbilityTestPanel } from './ability-test-panel';

type AliasRow = { alias: string; key: string };

type Props = {
  assetId: string;
  role?: 'owner' | 'editor' | 'viewer';
  asset?: AssetWithPermission | null;
  // Service context (optional, for published services)
  serviceSlug?: string;
  orgSlug?: string;
};

const normalizeAiState = (state?: AiSearchAbilityState): AiSearchAbilityState => {
  const searchActionId =
    (state?.searchActionId && state.searchActionId.trim().length ? state.searchActionId.trim() : undefined) ||
    (state?.actionId && state.actionId.trim().length ? state.actionId.trim() : undefined) ||
    'ai-search';
  const answerActionId =
    state?.answerActionId && state.answerActionId.trim().length ? state.answerActionId.trim() : 'ai-answer';
  return {
    enabled: !!state?.enabled,
    actionId: searchActionId,
    searchActionId,
    answerActionId,
    autoSyncOnChange: state?.autoSyncOnChange ?? false,
    status: state?.status ? { ...state.status, files: state.status.files ?? [] } : undefined,
  };
};

const normalizeContentState = (state?: ContentResolutionAbilityState): ContentResolutionAbilityState => ({
  enabled: !!state?.enabled,
  exposeContent: !!state?.exposeContent,
  exposeDeps: !!state?.exposeDeps,
  depWhitelist: state?.depWhitelist ?? [],
  depAliases: state?.depAliases ?? {},
  contentActionId: state?.contentActionId?.trim() || 'content',
  registryActionId: state?.registryActionId?.trim() || 'dependency',
});

const mapToAliasRows = (aliases: Record<string, string> | undefined | null): AliasRow[] =>
  aliases ? Object.entries(aliases).map(([alias, key]) => ({ alias, key })) : [];

const buildAliasMap = (rows: AliasRow[]): Record<string, string> => {
  const map: Record<string, string> = {};
  for (const row of rows) {
    const alias = row.alias.trim();
    const key = row.key.trim();
    if (alias && key) {
      map[alias] = key;
    }
  }
  return map;
};

const formatDateTime = (value?: string) => {
  if (!value) return 'Never synced';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const formatBytes = (value?: number) => {
  if (value === undefined || value === null) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let bytes = value;
  let unitIndex = 0;
  while (bytes >= 1024 && unitIndex < units.length - 1) {
    bytes /= 1024;
    unitIndex += 1;
  }
  const formatted = unitIndex === 0 ? bytes.toFixed(0) : bytes.toFixed(bytes < 10 ? 1 : 0);
  return `${formatted} ${units[unitIndex]}`;
};

export const AssetAbilitiesPanel = ({ assetId, role, asset, serviceSlug, orgSlug }: Props) => {
  const canEdit = role === 'owner' || role === 'editor';
  const canResync = role === 'owner';

  const [loading, setLoading] = useState(true);
  const [ai, setAi] = useState<AiSearchAbilityState>(normalizeAiState());
  const [content, setContent] = useState<ContentResolutionAbilityState>(normalizeContentState());
  const [aliasRows, setAliasRows] = useState<AliasRow[]>([]);
  const [savingAi, setSavingAi] = useState(false);
  const [savingContent, setSavingContent] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [pollingJobId, setPollingJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<{ status?: string; error?: string; jobLogs?: any[] } | null>(null);

  const defaultDependencyAliases: AliasRow[] = useMemo(() => {
    if (!asset || !asset.data || typeof asset.data !== 'object') {
      return [];
    }
    const deps = (asset.data as Record<string, unknown>).dependencies;
    if (deps && typeof deps === 'object') {
      return Object.keys(deps as Record<string, unknown>).map((key) => ({ alias: key, key }));
    }
    return [];
  }, [asset]);

  const syncAliasRows = (rows: AliasRow[]) => {
    setAliasRows(rows);
    setContent((prev) => ({
      ...prev,
      depAliases: buildAliasMap(rows),
    }));
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data: AbilitiesResponse = await abilityService.getAbilities(assetId);
        const nextAi = normalizeAiState(data.abilities['ai-search']);
        const baseContent = normalizeContentState(data.abilities['content-resolution']);

        let initialAliasRows = mapToAliasRows(baseContent.depAliases);
        if (!initialAliasRows.length) {
          initialAliasRows = defaultDependencyAliases.length ? defaultDependencyAliases : [{ alias: '', key: '' }];
        }

        setAi(nextAi);
        setContent({
          ...baseContent,
          depAliases: buildAliasMap(initialAliasRows),
        });
        setAliasRows(initialAliasRows);

        // Fetch current job status to show on page load
        try {
          const jobResponse = await fetch(`/api/assets/${assetId}/abilities/ai-search/current-job`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          });

          if (jobResponse.ok) {
            const jobData = await jobResponse.json();
            if (jobData.jobId) {
              setJobStatus({
                status: jobData.status,
                error: jobData.error,
                jobLogs: jobData.jobLogs,
              });
              // Only start polling if job is still processing (not completed)
              if (jobData.status === 'processing' || jobData.status === 'pending') {
                setPollingJobId(jobData.jobId);
              }
            }
          }
        } catch (e) {
          console.error('Failed to fetch current job status', e);
          // Don't fail the whole page load if job status fetch fails
        }
      } catch (e) {
        console.error('Failed to load abilities', e);
        toast.error(e instanceof Error ? e.message : 'Failed to load abilities');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [assetId, defaultDependencyAliases]);

  // Polling effect for job status
  useEffect(() => {
    if (!pollingJobId) return;

    const pollJobStatus = async () => {
      try {
        const response = await fetch(
          `/api/assets/${assetId}/abilities/ai-search/job-status/${pollingJobId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }

        const data = await response.json();

        // Update job status separately (not in ai.status)
        // Keep existing jobLogs and merge with new data
        setJobStatus((prev) => ({
          status: data.status,
          error: data.error,
          jobLogs: data.jobLogs || prev?.jobLogs,
        }));

        // Stop polling if job is complete
        if (data.status === 'indexed' || data.status === 'failed') {
          setPollingJobId(null);
          setResyncing(false);
          if (data.status === 'indexed') {
            toast.success('Sync job completed successfully');
          } else {
            toast.error(`Sync job failed: ${data.error || 'Unknown error'}`);
          }
        }
      } catch (e) {
        console.error('Failed to poll job status', e);
        // Continue polling even on error, but show a warning after a few retries
        // For now, just log and continue
      }
    };

    // Poll every 3 seconds
    const interval = setInterval(pollJobStatus, 3000);

    // Fetch immediately on start
    pollJobStatus();

    return () => clearInterval(interval);
  }, [pollingJobId, assetId]);

  const whitelistText = useMemo(() => (content.depWhitelist || []).join('\n'), [content.depWhitelist]);
  const syncedFiles = ai.status?.files ?? [];

  const parseWhitelist = (text: string): string[] =>
    text.split('\n').map((s) => s.trim()).filter(Boolean);

  const saveAi = async () => {
    try {
      setSavingAi(true);
      const searchActionId = ai.searchActionId?.trim() || ai.actionId?.trim() || 'ai-search';
      const answerActionId = ai.answerActionId?.trim() || 'ai-answer';
      const res = await abilityService.updateAiSearch(assetId, {
        enabled: ai.enabled,
        autoSyncOnChange: ai.autoSyncOnChange,
        searchActionId,
        answerActionId,
        actionId: searchActionId,
      });
      setAi(normalizeAiState(res.state));
      toast.success('AI Search settings saved');
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Failed to save AI Search settings');
    } finally {
      setSavingAi(false);
    }
  };

  const doResync = async () => {
    try {
      setResyncing(true);
      setJobStatus(null);
      const res = await abilityService.resyncAiSearch(assetId);
      const nextState = normalizeAiState(res.state);
      setAi(nextState);

      // Check if there's a sync job ID in the response
      const syncJobId = (res as any).syncJobId;
      if (syncJobId) {
        setPollingJobId(syncJobId);
        toast.success('Files synced, monitoring job status...');
      } else {
        // No job ID (shouldn't normally happen)
        toast.success('Resync completed');
        setResyncing(false);
      }
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Failed to resync');
      setResyncing(false);
    }
  };

  const saveContent = async () => {
    try {
      setSavingContent(true);
      const payload: ContentResolutionAbilityState = {
        ...content,
        contentActionId: content.contentActionId?.trim() || 'content',
        registryActionId: content.registryActionId?.trim() || 'dependency',
        depWhitelist: content.depWhitelist ?? [],
        depAliases: buildAliasMap(aliasRows),
      };
      const res = await abilityService.updateContentResolution(assetId, payload);
      const next = normalizeContentState(res.state);
      setContent(next);
      const nextRows = mapToAliasRows(next.depAliases);
      setAliasRows(nextRows.length ? nextRows : [{ alias: '', key: '' }]);
      toast.success('Content resolution settings saved');
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Failed to save content settings');
    } finally {
      setSavingContent(false);
    }
  };

  const handleAliasChange = (index: number, field: 'alias' | 'key', value: string) => {
    const updated = aliasRows.map((row, i) => (i === index ? { ...row, [field]: value } : row));
    syncAliasRows(updated);
  };

  const addAliasRow = () => {
    syncAliasRows([...aliasRows, { alias: '', key: '' }]);
  };

  const removeAliasRow = (index: number) => {
    const updated = aliasRows.filter((_, i) => i !== index);
    syncAliasRows(updated.length ? updated : [{ alias: '', key: '' }]);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Abilities</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="configuration" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="configuration">Configuration</TabsTrigger>
              <TabsTrigger value="testing">Testing</TabsTrigger>
            </TabsList>

            {/* Configuration Tab */}
            <TabsContent value="configuration" className="space-y-8 mt-6">
              {/* AI Search */}
              <section className="space-y-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h3 className="text-base font-semibold">AI Search</h3>
                <p className="text-sm text-muted-foreground">Configure search action and synced content for Cloudflare AI.</p>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="ai-enabled" checked={!!ai.enabled} onCheckedChange={(v) => setAi((s) => ({ ...s, enabled: !!v }))} disabled={!canEdit || loading || savingAi} />
                <Label htmlFor="ai-enabled">Enabled</Label>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="flex items-center gap-2">
                <Checkbox id="ai-autosync" checked={!!ai.autoSyncOnChange} onCheckedChange={(v) => setAi((s) => ({ ...s, autoSyncOnChange: !!v }))} disabled={!canEdit || loading || savingAi} />
                <Label htmlFor="ai-autosync">Auto sync on change</Label>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ai-search-action">Vector search action ID</Label>
                <Input
                  id="ai-search-action"
                  value={ai.searchActionId ?? ''}
                  onChange={(event) =>
                    setAi((s) => ({
                      ...s,
                      searchActionId: event.target.value,
                      actionId: event.target.value,
                    }))
                  }
                  placeholder="ai-search"
                  disabled={!canEdit || loading || savingAi}
                />
                <p className="text-xs text-muted-foreground">Routes to the vector-only endpoint `/api/svc/&lt;org&gt;/&lt;slug&gt;/[id]`.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ai-answer-action">Answer action ID</Label>
                <Input
                  id="ai-answer-action"
                  value={ai.answerActionId ?? ''}
                  onChange={(event) => setAi((s) => ({ ...s, answerActionId: event.target.value }))}
                  placeholder="ai-answer"
                  disabled={!canEdit || loading || savingAi}
                />
                <p className="text-xs text-muted-foreground">Routes to the generative answer endpoint (Workers `aiSearch`).</p>
              </div>
            </div>

            <div className="grid gap-2 md:grid-cols-3">
              <div>
                <Label>Last Synced</Label>
                <div className="mt-1 text-sm text-muted-foreground">{formatDateTime(ai.status?.lastSyncedAt)}</div>
              </div>
              <div>
                <Label>Objects</Label>
                <div className="mt-1 text-sm text-muted-foreground">{ai.status?.objectCount ?? 0}</div>
              </div>
              <div>
                <Label>Total Bytes</Label>
                <div className="mt-1 text-sm text-muted-foreground">{formatBytes(ai.status?.totalBytes)}</div>
              </div>
            </div>

            {(pollingJobId || jobStatus) && (
              <div className="space-y-3 p-3 rounded-md border border-blue-200 bg-blue-50">
                <div className="grid gap-2 md:grid-cols-2">
                  <div>
                    <Label className="text-xs">Sync Job ID</Label>
                    <div className="mt-1 text-sm text-muted-foreground font-mono">{pollingJobId || 'Loading...'}</div>
                  </div>
                  <div>
                    <Label className="text-xs">Job Status</Label>
                    <div className={`mt-1 text-sm font-medium capitalize ${
                      jobStatus?.status === 'indexed'
                        ? 'text-green-600'
                        : jobStatus?.status === 'failed'
                        ? 'text-red-600'
                        : 'text-blue-600'
                    }`}>
                      {jobStatus?.status || 'pending'}
                    </div>
                  </div>
                </div>

                {jobStatus?.jobLogs && (
                  <div className="space-y-2">
                    <Label className="text-xs">Sync Progress ({jobStatus.jobLogs.length} logs)</Label>
                    <div className="max-h-40 overflow-y-auto p-2 bg-white rounded border border-blue-100 text-xs font-mono">
                      {jobStatus.jobLogs.length > 0 ? (
                        jobStatus.jobLogs.map((log: any, idx: number) => (
                          <div key={idx} className="text-muted-foreground py-1">
                            {log.message || JSON.stringify(log)}
                          </div>
                        ))
                      ) : (
                        <div className="text-muted-foreground italic">No logs available yet</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {jobStatus?.error && (
              <div>
                <Label>Sync Error</Label>
                <div className="mt-1 p-2 rounded-md bg-red-50 border border-red-200">
                  <p className="text-sm text-red-700">{jobStatus.error}</p>
                </div>
              </div>
            )}

            {ai.status?.warnings && ai.status.warnings.length > 0 && (
              <div>
                <Label>Warnings</Label>
                <ul className="mt-1 list-disc list-inside text-sm text-muted-foreground space-y-1">
                  {ai.status.warnings.map((warning, index) => (
                    <li key={`ai-warning-${index}`}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            {syncedFiles.length > 0 && (
              <div className="space-y-2">
                <Label>Synced Files</Label>
                <div className="overflow-hidden rounded-md border">
                  <div className="grid gap-2 bg-muted px-3 py-2 text-xs font-medium md:grid-cols-[2fr_2fr_auto] uppercase text-muted-foreground">
                    <span>Source</span>
                    <span>Destination</span>
                    <span>Size</span>
                  </div>
                  {syncedFiles.map((file, index) => (
                    <div
                      key={`${file.destinationKey}-${index}`}
                      className="grid gap-2 px-3 py-2 text-sm md:grid-cols-[2fr_2fr_auto] border-t"
                    >
                      <span className="truncate" title={file.sourceKey}>{file.sourceKey}</span>
                      <span className="truncate" title={file.destinationKey}>{file.destinationKey}</span>
                      <span>{formatBytes(file.bytes)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={saveAi} disabled={!canEdit || loading || savingAi}>{savingAi ? 'Saving...' : 'Save'}</Button>
              <Button variant="outline" onClick={doResync} disabled={!canResync || loading || resyncing || !ai.enabled}>{resyncing ? 'Resyncing...' : 'Resync'}</Button>
            </div>
          </section>

          <Separator />

          {/* Content Resolution */}
          <section className="space-y-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h3 className="text-base font-semibold">Content Resolution</h3>
                <p className="text-sm text-muted-foreground">Control which files can be fetched via public gated actions.</p>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="cr-enabled" checked={!!content.enabled} onCheckedChange={(v) => setContent((s) => ({ ...s, enabled: !!v }))} disabled={!canEdit || loading || savingContent} />
                <Label htmlFor="cr-enabled">Enabled</Label>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex items-center gap-2">
                <Checkbox id="cr-content" checked={!!content.exposeContent} onCheckedChange={(v) => setContent((s) => ({ ...s, exposeContent: !!v }))} disabled={!canEdit || loading || !content.enabled || savingContent} />
                <Label htmlFor="cr-content">Expose main content</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="cr-deps" checked={!!content.exposeDeps} onCheckedChange={(v) => setContent((s) => ({ ...s, exposeDeps: !!v }))} disabled={!canEdit || loading || !content.enabled || savingContent} />
                <Label htmlFor="cr-deps">Expose dependencies via registry</Label>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="cr-content-action">Content action ID</Label>
                <Input
                  id="cr-content-action"
                  value={content.contentActionId ?? ''}
                  onChange={(event) => setContent((s) => ({ ...s, contentActionId: event.target.value }))}
                  placeholder="content"
                  disabled={!canEdit || loading || savingContent}
                />
                <p className="text-xs text-muted-foreground">POST `/api/svc/&lt;org&gt;/&lt;slug&gt;/[id]` streams the main file.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cr-registry-action">Dependency action ID</Label>
                <Input
                  id="cr-registry-action"
                  value={content.registryActionId ?? ''}
                  onChange={(event) => setContent((s) => ({ ...s, registryActionId: event.target.value }))}
                  placeholder="dependency"
                  disabled={!canEdit || loading || savingContent}
                />
                <p className="text-xs text-muted-foreground">
                  GET `/api/svc/&lt;org&gt;/&lt;slug&gt;/[id]/&lt;dependencyKey&gt;` (or POST with body) streams a dependency.
                </p>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="cr-whitelist">Whitelist (one per line)</Label>
                <Textarea
                  id="cr-whitelist"
                  placeholder="asset-uuid/\nasset-uuid/some-prefix/"
                  value={whitelistText}
                  onChange={(e) => setContent((s) => ({ ...s, depWhitelist: parseWhitelist(e.target.value) }))}
                  disabled={!canEdit || loading || !content.enabled || savingContent}
                  rows={6}
                />
                <p className="mt-2 text-xs text-muted-foreground">Requests must target keys or prefixes listed here.</p>
              </div>
              <div className="space-y-2">
                <Label>Dependency Aliases</Label>
                <div className="space-y-2">
                  {aliasRows.map((row, index) => (
                    <div key={`alias-row-${index}`} className="grid gap-2 md:grid-cols-[1fr_1fr_auto]">
                      <Input
                        placeholder="Alias"
                        value={row.alias}
                        onChange={(event) => handleAliasChange(index, 'alias', event.target.value)}
                        disabled={!canEdit || loading || savingContent}
                      />
                      <Input
                        placeholder="Dependency key"
                        value={row.key}
                        onChange={(event) => handleAliasChange(index, 'key', event.target.value)}
                        disabled={!canEdit || loading || savingContent}
                      />
                      <Button
                        variant="ghost"
                        onClick={() => removeAliasRow(index)}
                        disabled={!canEdit || loading || savingContent || aliasRows.length <= 1}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Aliases map to dependency keys when calling `/api/svc/&lt;org&gt;/&lt;slug&gt;/[id]/&lt;alias&gt;`.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addAliasRow}
                  disabled={!canEdit || loading || savingContent}
                >
                  Add alias
                </Button>
              </div>
            </div>

            <div>
              <Button onClick={saveContent} disabled={!canEdit || loading || savingContent}>{savingContent ? 'Saving...' : 'Save'}</Button>
            </div>
          </section>
            </TabsContent>

            {/* Testing Tab */}
            <TabsContent value="testing" className="mt-6">
              <AbilityTestPanel
                assetId={assetId}
                asset={asset}
                serviceSlug={serviceSlug}
                orgSlug={orgSlug}
                abilities={{ 'ai-search': ai, 'content-resolution': content }}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default AssetAbilitiesPanel;
